// src/App.tsx
import { Box, CssBaseline } from "@mui/material";
import React from "react";
import { AppBarSection } from "./components/appBar";
import PageContent from "./components/page-content/PageContent";
import PageContentBox from "./components/page-content/PageContentBox";
import { SideNav } from "./components/sideNav";
import { AiPanel, AI_PANEL_WIDTH, useAiPanelNarrow } from "./components/ai-panel/AiPanel";
import {
  useFetchAppListener,
  useFetchAuthToken,
  useSetInitialPage,
} from "./hook";
import { useDrawerWidth } from "./hook/layout";
import { useInitCustomer } from "./hook/settings";
import { useAiPanelFullscreen, useAiPanelOpen } from "./hook/ai-panel";
import { AiConnectionProvider } from "./connection/AiConnectionProvider";
import { WorkspaceSwitchingProvider } from "./context/workspaceSwitching";
import { ActiveEditorProvider } from "./context/activeEditor";
import { PhoneViewProvider, useIsPhone } from "./context/phoneView";
import { PhoneShell } from "./components/phone/PhoneShell";
import { LegacyClaudeAuthCleanup } from "./lib/legacyAuthStorage";

export const AppInternal: React.FC = () => {
  const drawerWidth = useDrawerWidth();
  const [aiOpen] = useAiPanelOpen();
  const [aiFullscreen] = useAiPanelFullscreen();
  const aiNarrow = useAiPanelNarrow();
  const isPhone = useIsPhone();

  // On a phone the panel covers the screen; reserving 420px beside the page made the page wider
  // than the screen, so it scrolled sideways behind the panel (agent-chat-speed Decision 8).
  const peakedWidth = aiOpen && !aiFullscreen && !aiNarrow ? AI_PANEL_WIDTH : 0;

  return (
    <WorkspaceSwitchingProvider>
      <AiConnectionProvider>
        <ActiveEditorProvider>
          <PhoneViewProvider>
            <CssBaseline />
            {isPhone ? (
              <>
                <AppBarSection drawerWidth="0" />
                <PhoneShell />
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    marginRight: `${peakedWidth}px`,
                    transition: "margin-right 0.2s ease",
                  }}
                >
                  <SideNav drawerWidth={drawerWidth} />
                  <AppBarSection drawerWidth={drawerWidth} />
                  <PageContentBox drawerWidth={drawerWidth}>
                    <PageContent />
                  </PageContentBox>
                </Box>
                <AiPanel />
              </>
            )}
          </PhoneViewProvider>
        </ActiveEditorProvider>
      </AiConnectionProvider>
    </WorkspaceSwitchingProvider>
  );
};

const App: React.FC = () => {
  useFetchAppListener();
  useFetchAuthToken();
  useSetInitialPage();
  useInitCustomer();

  return (
    <>
      <LegacyClaudeAuthCleanup />
      <AppInternal />
    </>
  );
};

export default App;
